import { useEffect, useRef } from 'react';

import { analytics } from '@opendoor/observability';

import { useAppSelector } from 'src/store/hooks';
import { SavedAnswer } from 'src/store/sellerInput';

/**
 * A custom hook to wrap trackEvent in a useEffect
 */
function useTrackAnswer<AdditionalDepsType = unknown>(
  answer: SavedAnswer,
  eventData: {
    category: string;
    action: string;
    value?: any;
    extra?: any;
  },
  opts?: {
    trackWhenFn?: (answer: SavedAnswer, ...additionalDeps: Array<AdditionalDepsType>) => boolean;
    onTrack?: (answer: SavedAnswer, ...additionalDeps: Array<AdditionalDepsType>) => void;
    additionalDeps?: Array<AdditionalDepsType>;
  },
) {
  const { category, action, value, extra } = eventData;
  const mounted = useRef(false);
  useEffect(
    () => {
      // only track values when they change, ignore the initial mount
      if (!mounted.current) {
        mounted.current = true;
        return;
      }
      const { trackWhenFn, onTrack, additionalDeps } = {
        trackWhenFn: (answer: SavedAnswer) => !!answer,
        onTrack: () => {},
        additionalDeps: [],
        ...opts,
      };

      if (trackWhenFn(answer, ...additionalDeps)) {
        analytics.trackEvent(category, action, value, extra);
        onTrack(answer, ...additionalDeps);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [answer, category, action, value, JSON.stringify(extra), ...(opts?.additionalDeps ?? [])],
  );
}

const TrackAnswerChange = () => {
  const answers = useAppSelector((state) => state.sellerInput.savedAnswers);
  const completedSlug = useAppSelector((state) => state.sellerInput.lastCompletedSlug);

  const leadState = useAppSelector((state) => state.sellerInput.leadState);
  const denialCode = useAppSelector((state) => state.sellerInput.denialCode);
  const denialReason = useAppSelector((state) => state.sellerInput.denialReason);

  const activelyLooking = answers['seller.actively_looking'];
  useTrackAnswer(
    activelyLooking,
    {
      category: 'buy-sell',
      action: 'actively-looking',
      extra: { activelyLooking },
    },
    {
      trackWhenFn: (answer, completedSlug) => !!answer && completedSlug == 'buying-timeline',
      additionalDeps: [completedSlug],
    },
  );

  // This seriousSeller variable is used in Google Tag Manager
  const saleTimeline = answers['seller.sale_timeline'];
  useTrackAnswer(saleTimeline, {
    category: 'sale-timeline',
    action: 'serious-seller',
    extra: {
      seriousSeller: saleTimeline !== 'Just Browsing',
      saleTimeline,
    },
  });

  // This homebuilder variable is used in Google Tag Manager
  const homebuilderAnswer = answers['seller.working_with_home_builder'];
  useTrackAnswer(homebuilderAnswer, {
    category: 'home-builder',
    action: 'working-with-homebuilder',
    extra: {
      workingWithHomebuilder: homebuilderAnswer,
    },
  });

  const email = answers['seller.email'];
  useTrackAnswer(
    email,
    {
      category: 'lead-state',
      action: 'email',
      value: 'ready',
      extra: { email },
    },
    {
      onTrack: (answer) => {
        analytics.register({ email: answer });
      },
    },
  );

  const category = 'seller_onboarding';
  useTrackAnswer(email, {
    category,
    action: 'email_conversion',
    value: 'optimizely-web',
    extra: email,
  });

  /* lead denial tracking */
  useEffect(() => {
    if (leadState === 'denied') {
      analytics.register({
        seller_flow_denial_code: denialCode,
        seller_flow_denial_reason: denialReason,
        seller_flow_denied: true,
      });
    }
  }, [leadState, denialCode, denialReason]);

  return null;
};

export default TrackAnswerChange;
